import ButtonComponent from '../../../components/ButtonComponent';
import { Grid2 } from '@mui/material';
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import zod from 'zod';
import { DeviceVideoConfigFormSchema } from '../../../services/Devices/schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useGetVideoConfigAsync,
  useUpdateVideoConfigAsync,
} from '../../../services/Devices/hooks/useMutation';
import { AppContext } from '../../../context/AppContextProvider';
import { NumberFieldComponent } from '../../../components/NumberFieldComponent';
import DropSelectComponent, {
  IDropSelectOptions,
} from '../../../components/DropSelectComponent';

interface DeviceVideoConfigProps {
  id: number;
  setIsPendingChanges: Dispatch<SetStateAction<boolean>>;
  setFunctionToConfirmPendingChanges: Dispatch<SetStateAction<() => void>>;
}

type DeviceVideoConfigFormInput = zod.infer<typeof DeviceVideoConfigFormSchema>;

const DeviceVideoConfig = ({
  id,
  setIsPendingChanges,
  setFunctionToConfirmPendingChanges,
}: DeviceVideoConfigProps) => {
  const { showSnackbar } = useContext(AppContext);
  const {
    control,
    reset,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<DeviceVideoConfigFormInput>({
    resolver: zodResolver(DeviceVideoConfigFormSchema),
    defaultValues: {},
  });
  const [resolutionSelected, setResolutionSelected] = useState<
    string | undefined
  >();

  const framesWatch = watch('frame');
  const {
    mutateAsync: mutateGetVideoConfigAsync,
    isLoading: isLoadingGetVideoConfigAsync,
  } = useGetVideoConfigAsync();

  const {
    mutateAsync: mutateUpdateVideoConfigAsync,
    isLoading: isLoadingUpdateVideoConfigAsync,
  } = useUpdateVideoConfigAsync();

  const compressionOptions: IDropSelectOptions[] = [
    { option: 'H.264', value: 'H264' },
    { option: 'H.265', value: 'H265' },
  ];

  const bitRateTypeOptions: IDropSelectOptions[] = [
    { option: 'CBR', value: 'CBR' },
    { option: 'VBR', value: 'VBR' },
  ];

  const bitRateOptions: () => IDropSelectOptions[] = () => {
    const options = [
      512, 640, 768, 896, 1024, 1280, 1536, 1792, 2048, 4096, 6144,
    ];
    return options.map((i) => ({ option: i.toString(), value: i }));
  };

  const resolutionOptions: IDropSelectOptions[] = [
    { option: '1920x1080 (1080P)', value: '1920,1080' },
    { option: '1280x960 (1.3M)', value: '1280,960' },
    { option: '1280x720 (720P)', value: '1280,720' },
  ];

  const handleSave = (data: DeviceVideoConfigFormInput) => {
    mutateUpdateVideoConfigAsync(
      { id, data },
      {
        onSuccess: () => {
          showSnackbar(
            'Configuração de vídeo atualizada com sucesso.',
            'success',
          );
          setIsPendingChanges(false);
        },
      },
    );
  };

  const onChangeResolution = (value: string) => {
    setResolutionSelected(value);
    const spl = value.split(',');
    setValue('widthScreen', Number.parseInt(spl[0]));
    setValue('heightScreen', Number.parseInt(spl[1]));
  };

  useEffect(() => {
    if (id)
      mutateGetVideoConfigAsync(id).then((r) => {
        if (r.widthScreen && r.heightScreen) {
          setResolutionSelected(`${r.widthScreen},${r.heightScreen}`);
        }
        reset(r);
      });
  }, [id]);

  return (
    <Grid2 container columnSpacing={2} rowSpacing={3}>
      <Grid2 size={{ xl: 6, xs: 12 }}>
        <Controller
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DropSelectComponent
              label={'Tipo de compressão'}
              options={compressionOptions}
              isError={!!error}
              errorMessage={error?.message}
              valueSelected={value}
              isRequired
              onChange={onChange}
            />
          )}
          name={'encodingType'}
          control={control}
        />
      </Grid2>
      <Grid2 size={{ xl: 6, xs: 12 }}>
        <DropSelectComponent
          label={'Resolução'}
          isError={!!errors.widthScreen}
          errorMessage={errors.widthScreen?.message}
          options={resolutionOptions}
          valueSelected={resolutionSelected}
          onChange={(value) => onChangeResolution(value)}
        />
      </Grid2>
      <Grid2 size={{ xl: 6, xs: 12 }}>
        <Controller
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DropSelectComponent
              label={'Tipo de taxa de bits'}
              options={bitRateTypeOptions}
              isError={!!error}
              errorMessage={error?.message}
              valueSelected={value}
              onChange={onChange}
            />
          )}
          name={'bitRateType'}
          control={control}
        />
      </Grid2>
      <Grid2 size={{ xl: 6, xs: 12 }}>
        <Controller
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <NumberFieldComponent
              label="Taxa de frames (kb/s)"
              value={value}
              errorMessage={error?.message}
              minValue={1}
              maxValue={30}
              onValueChange={onChange}
            />
          )}
          name={'frame'}
          control={control}
        />
      </Grid2>
      <Grid2 size={{ xl: 6, xs: 12 }}>
        <Controller
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DropSelectComponent
              label={'Taxa de bits (kb/s)'}
              options={bitRateOptions()}
              errorMessage={error?.message}
              isError={!!error}
              valueSelected={value}
              onChange={onChange}
            />
          )}
          name={'bitRate'}
          control={control}
        />
      </Grid2>
      <Grid2 size={{ xl: 6, xs: 12 }}>
        <Controller
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <NumberFieldComponent
              label={`Intervalo do frame (${framesWatch}~150)`}
              value={value}
              errorMessage={error?.message}
              minValue={framesWatch}
              maxValue={150}
              onValueChange={onChange}
            />
          )}
          name={'frameInterval'}
          control={control}
        />
      </Grid2>
      <Grid2 size={12} sx={{ mt: 1 }}>
        <ButtonComponent
          fullWidth
          onClick={handleSubmit(handleSave)}
          loading={
            isLoadingGetVideoConfigAsync || isLoadingUpdateVideoConfigAsync
          }
        >
          Salvar
        </ButtonComponent>
      </Grid2>
    </Grid2>
  );
};

export default DeviceVideoConfig;
